import type * as pf from '@amirada/lib-shared'
import type { ExtCmd, ExtCmdType } from '@amirada/lib-wilo-shared'
import { ut_addUrlParams, ut_copyParams } from '@base/utils/utils'
import type * as tp from '@base/utils/types'

import { useAppIndexStore } from '@base/stores/indexStore'
import { ENotificationType } from '@base/utils/constants'

/*
 * The options of a call: base's IFetchData plus what only this client needs, kept here so base stays
 * untouched.
 *   timeout   ms until the request is given up (ofetch `timeout`) and fails like any other error —
 *             for calls that must not wait for ever on a server that accepts the connection but never
 *             answers (price-list auto-save, 14.10.2026). Unset = no limit, as before.
 */
export type TFetchData = tp.IFetchData & { timeout?: number }

export function usePfinderAPI() {
  const runtimeConfig = useRuntimeConfig()
  // Read here, in the factory: inside fetchData, after an await, the Nuxt instance may be gone on the server.
  const botParam: string = useAppConfig().botEmbParam || ''
  const indexStore = useAppIndexStore()

  // SSR: pass the incoming end-user IP on to pfinder, so its per-IP accounting sees the real user
  // instead of the one Nuxt cluster IP. In the browser useRequestHeaders gives {} — there $fetch
  // carries the real IP by itself.
  //
  // x-pfinder-caller-ip / x-pfinder-caller-ua: the same caller under own names, set by the CMS
  // embedding (deploy/cms/index.php) because a reverse proxy may overwrite the standard headers but
  // passes own names through. The caller's agent travels only under its own name: our own
  // User-Agent stays, so the pfinder log still tells the SSR gateway from a direct browser call.
  const ssrHeaders: Record<string, string> = import.meta.server
    ? useRequestHeaders(['x-forwarded-for', 'x-real-ip', 'x-pfinder-caller-ip', 'x-pfinder-caller-ua'])
    : {}
  const fwdHeaders: Record<string, string> = {}
  for (const name of ['x-forwarded-for', 'x-real-ip', 'x-pfinder-caller-ip', 'x-pfinder-caller-ua']) {
    if (ssrHeaders[name]) { fwdHeaders[name] = ssrHeaders[name] }
  }

  // Why the failure of the LAST call is kept here at all: `fetchData` swallows every error into a
  // notification and hands back `undefined`, so a caller learns THAT it failed and never WHY. And
  // the notification does not reach the customer: `AppIndex.vue` renders `<Notifications>` only
  // when NOT embedded, and embedded is exactly how the cms runs us. Without this, an error is
  // invisible to the one person who has to report it.
  // A ref rather than a return value keeps `fetchData`'s contract intact — every existing caller
  // just checks the result for falsy. `usePfinderAPI()` is a plain factory, so this belongs to the
  // one component that called it, and reading it right after an awaited call is unambiguous.
  const lastError: Ref<{ status: number, message: string } | undefined> = ref()

  /*
   * Every request shows the global loading state, COUNTED (indexStore.withLoading, 14.10.2026): a
   * request inside someone else's bracket no longer switches the overlay off when it is done, so
   * `noLoading` is no longer needed for that. It stays for what must never show the overlay at all —
   * background work like the price-list auto-save.
   */
  // @ts-expect-error ToDo check ExtCmd should work
  async function fetchData<T extends ExtCmdType>(cmd: T, args: ExtCmd<T>['arg'], data: TFetchData): Promise<pf.ResponseData & { result: ExtCmd<T>['res'] } | undefined> {
    const request = async (): Promise<pf.ResponseData & { result: ExtCmd<T>['res'] } | undefined> => {
      lastError.value = undefined
      const paramsQuery: tp.IParamQueryServer = data.paramsQuery || {}
      const paramData: tp.IParamData = data.paramData || indexStore.state.paramData
      // SSR only: a caller the CMS marked as a bot travels as x-client-bot, so the server can turn bots
      // away before people when it is overloaded (see botEmbParam in the app.config). Never in the
      // browser: an own header on a cross-origin call would cost a CORS preflight per request.
      const isBot: boolean = import.meta.server && !!botParam && (paramData.embeddedInfo as Record<string, string | undefined> | undefined)?.[botParam] === 'true'
      const headers: Record<string, string> = isBot ? { ...fwdHeaders, 'x-client-bot': '1' } : fwdHeaders

      // Set the actual country and language if nothing is set
      if (!paramsQuery.rcco) { paramsQuery.rcco = paramData.rcco }
      if (!paramsQuery.lcda) { paramsQuery.lcda = paramData.lcda }

      // standard URLServer params, only copy if not call without url params
      if (!data.noUrlParams) {
        ut_copyParams(paramData.queryUrlServer, paramsQuery, paramData.queryUrlServer, false, true)
      }

      if (paramData.queryUrlServer.stage) {
        paramsQuery.stage = paramsQuery.stage || paramData.queryUrlServer.stage // always set stage even if there is without urlparam
      }

      const url: string = [runtimeConfig.public.dataHost || '', runtimeConfig.public.pfinderService || '', <T>(cmd)].join('/')
      const method = data.usePost ? 'POST' : 'GET'
      // const headers = data.headers || { 'Content-Type': 'application/json' }

      try {
        let res: pf.ResponseData & { result: ExtCmd<T>['res'] }
        if (method === 'GET') {
          const params: any = { ...args, ...paramsQuery } // join args and params all get parameters
          res = await $fetch<pf.ResponseData & { result: ExtCmd<T>['res'] }>(ut_addUrlParams(url, params), { method, headers, timeout: data.timeout })
        }
        else {
          // args as post and params as get
          // @ts-expect-error ToDo check ExtCmd should work
          res = await $fetch<pf.ResponseData & { result: ExtCmd<T>['res'] }>(ut_addUrlParams(url, paramsQuery), { method, headers, body: args, timeout: data.timeout }) as pf.ResponseData & { result: ExtCmd<T>['res'] }
        }
        return res
      }
      catch (err) {
        const e = err as Error & { data?: { message?: string }, status?: number, statusCode?: number }
        console.error(e)
        // Prefer the server's own message over the transport one — same as the base composable.
        // $fetch puts the parsed response body on err.data, while e.message is only
        // '[GET] "<url>": 500 Internal Server Error'. This is the override the wilo client actually
        // uses (QuickSelectInput imports ~/composables/pfinderAPI), so fixing only base/ would have
        // left QuickSelect showing the useless transport text.
        const notification: tp.INotification = {
          title: 'Error fetching data',
          message: e.data?.message || e.message,
          id: JSON.stringify(url),
          type: ENotificationType.nt_warning,
        }
        indexStore.setNotification(notification)
        // ofetch names it `statusCode`, older shapes `status`; 0 means "no status came back at all"
        // (a network failure), which is worth telling apart from any http answer.
        lastError.value = { status: e.statusCode ?? e.status ?? 0, message: notification.message }
        return undefined
      }
    }
    return data.noLoading ? request() : indexStore.withLoading(request)
  }

  return {
    fetchData,
    lastError,
  }
}
